import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import NativeDateField, { isoToMDY } from './common/NativeDateField';

dayjs.extend(customParseFormat);

const getToday = () => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // YYYY-MM-DD
};

const round2 = (n) => Math.round(n * 100) / 100;
const UNALLOCATED_AUTO_DISTRIBUTION_THRESHOLD = 0.5;

/** Employee columns: highest Hours to Allocate (totalHours) first. */
const sortEmployeesByHoursToAllocate = (employees) =>
    [...(employees || [])].sort((a, b) => {
        const hoursA = Number(a.totalHours) || 0;
        const hoursB = Number(b.totalHours) || 0;
        if (hoursB !== hoursA) return hoursB - hoursA;
        return String(a.name || '').localeCompare(String(b.name || ''), undefined, {
            sensitivity: 'base',
        });
    });

const buildLaborRowsFromGrid = (laborGrid, laborDescriptions) => {
    const grid = laborGrid || [];
    const descriptions = laborDescriptions || [];
    if (descriptions.length > 0) {
        return descriptions.map((desc, idx) => {
            const baseRow = grid[idx] || {
                activityId: `desc-${idx}`,
                totalHours: 0,
                hoursByEmployee: {}
            };
            return {
                ...baseRow,
                activityLabel: desc
            };
        });
    }
    return grid;
};

const cloneHoursMap = (src) => {
    const out = {};
    Object.keys(src || {}).forEach((activityId) => {
        out[activityId] = { ...(src[activityId] || {}) };
    });
    return out;
};

const PARTIAL_HOURS_INPUT_RE = /^\d*\.?\d*$/;

const isValidPartialHoursInput = (value) => value === '' || PARTIAL_HOURS_INPUT_RE.test(value);

const normalizeHoursOnBlur = (value) => {
    if (value === '' || value == null) return 0;
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
};

const getLaborHoursCellValue = (editableRow, empKey, empId, fallbackHours) => {
    if (
        editableRow &&
        (Object.prototype.hasOwnProperty.call(editableRow, empKey) ||
            Object.prototype.hasOwnProperty.call(editableRow, empId))
    ) {
        const raw = editableRow[empKey] ?? editableRow[empId];
        if (raw === '') return '';
        if (raw == null) return 0;
        return raw;
    }
    const fallback = fallbackHours?.[empKey] ?? fallbackHours?.[empId];
    if (fallback === '' || fallback == null) return 0;
    return Math.max(0, Number(fallback) || 0);
};

/**
 * When project labor cells sum to slightly more or less than Hours to Allocate (rounding),
 * adjust the row with the largest hours so totals match and we never write a negative on an empty row.
 */
const reconcileLaborHoursToTargets = (hoursMap, laborRows, employees) => {
    const next = cloneHoursMap(hoursMap);
    const SLACK = 0.02;

    employees.forEach((emp) => {
        const empId = emp.id;
        const target = Number(emp.totalHours) || 0;
        let sum = 0;
        laborRows.forEach((row) => {
            const v = next[row.activityId]?.[empId];
            const num = v === '' || v == null ? 0 : Number(v);
            const n = Number.isFinite(num) ? num : 0;
            sum += Math.max(0, n);
        });
        sum = round2(sum);
        const delta = round2(target - sum);
        if (Math.abs(delta) <= 0.00005) return;
        if (Math.abs(delta) > SLACK) return;

        const scored = laborRows.map((row) => {
            const v = next[row.activityId]?.[empId];
            const num = v === '' || v == null ? 0 : Number(v);
            const n = Number.isFinite(num) ? num : 0;
            return { row, v: Math.max(0, n) };
        });
        scored.sort((a, b) => b.v - a.v);

        for (let i = 0; i < scored.length; i++) {
            const { row, v } = scored[i];
            const nv = round2(Math.max(0, v + delta));
            if (nv >= 0) {
                if (!next[row.activityId]) next[row.activityId] = {};
                next[row.activityId][empId] = nv;
                return;
            }
        }
        const row0 = laborRows[0];
        if (row0) {
            if (!next[row0.activityId]) next[row0.activityId] = {};
            const v = Number(next[row0.activityId]?.[empId]) || 0;
            next[row0.activityId][empId] = round2(Math.max(0, v + delta));
        }
    });
    employees.forEach((emp) => {
        const empId = emp.id;
        laborRows.forEach((row) => {
            const v = next[row.activityId]?.[empId];
            if (v === '' || v == null) return;
            const n = Number(v);
            if (Number.isFinite(n) && n < 0) {
                if (!next[row.activityId]) next[row.activityId] = {};
                next[row.activityId][empId] = 0;
            }
        });
    });
    return next;
};

const LaborTrackingModal = ({ 
    isOpen, 
    onClose, 
    projectCode,
    projectName,
    rowData,
    projectActivities = [],
    onLogProduction = () => { }, 
    onOpenUpdateMultiple = () => { },
    refreshNonce = 0,
}) => {
    const { getAuthConfig, isReady } = useAuthFetch();
    const [workDate, setWorkDate] = useState(getToday());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [otherActivityLabor, setOtherActivityLabor] = useState(null);
    const [otherActivityLoading, setOtherActivityLoading] = useState(false);

    const [isAddLaborOpen, setIsAddLaborOpen] = useState(false);
    const [selectedLaborTypes, setSelectedLaborTypes] = useState([]);
    const [isAddingLaborTypes, setIsAddingLaborTypes] = useState(false);
    const [laborScopeCodesFromApi, setLaborScopeCodesFromApi] = useState([]);
    const [laborScopeCodesLoading, setLaborScopeCodesLoading] = useState(false);
    const [laborScopeCodesError, setLaborScopeCodesError] = useState(null);

    const [activeEmployee, setActiveEmployee] = useState(null);
    const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });

    const lastFetchedDateRef = useRef(null);
    const lastFetchedScopeRef = useRef(null);
    const lastRefreshNonceRef = useRef(refreshNonce);
    const laborRequestSeqRef = useRef(0);
    const nonProjectRequestSeqRef = useRef(0);
    const tableScrollRef = useRef(null);
    const customScrollRef = useRef(null);
    const customScrollInnerRef = useRef(null);
    const isSyncingScrollRef = useRef(false);

    // Editable hours per labor row per employee: { [activityId]: { [empId]: number } }
    const [editableHours, setEditableHours] = useState({});

    const [saving, setSaving] = useState(false);
    /** validationBlocks: { messageVariant: 'unallocatedRemaining'|'overAllocated', employeeNames: string[] }[] */
    const [saveMessage, setSaveMessage] = useState(null); // { type, text, validationBlocks? }

    const [nonProjectLabor, setNonProjectLabor] = useState(null);
    const [nonProjectLaborLoading, setNonProjectLaborLoading] = useState(false);
    const [nplRefreshNonce, setNplRefreshNonce] = useState(0);
    const [showCustomScrollbar, setShowCustomScrollbar] = useState(false);
    const [customScrollbarContentWidth, setCustomScrollbarContentWidth] = useState(0);
    /** Current activity context (dashboard row or Other Activity Labor link). */
    const [activeRowData, setActiveRowData] = useState(rowData);

    useEffect(() => {
        if (isOpen) {
            setActiveRowData(rowData);
        }
    }, [isOpen, rowData]);

    const scopeName = activeRowData?.scope || activeRowData?.originalData?.scope || null;
    const scopeId = activeRowData?.scopeId || activeRowData?.originalData?.scopeId || null;

    const handleOpenOtherActivityLabor = (activity) => {
        const nextScope = String(activity.displayName || activity.scope || '').trim();
        const nextScopeId = activity.scopeId ?? null;
        if (!nextScope) return;

        const currentScopeNorm = String(scopeName || '').trim().toLowerCase();
        const nextScopeNorm = nextScope.toLowerCase();
        if (
            nextScopeNorm === currentScopeNorm &&
            String(nextScopeId || '') === String(scopeId || '')
        ) {
            return;
        }

        setActiveRowData({
            scopeId: nextScopeId,
            scope: nextScope,
            originalData: {
                scopeId: nextScopeId,
                scope: nextScope,
            },
        });
        setSaveMessage(null);
        setEditableHours({});
        setActiveEmployee(null);
        setData(null);
        setOtherActivityLabor(null);
        setLoading(true);
        setError(null);
    };

    const fetchLaborEntry = React.useCallback(async ({ silent = false } = {}) => {
        if (!isReady) return;
        const requestSeq = ++laborRequestSeqRef.current;
        if (!silent) {
            setLoading(true);
        }
        setError(null);
        try {
            const base = process.env.REACT_APP_BACKEND_BASEURL;
            const path = `${base}/api/miter/production-performance/${projectCode}/labor-entry/${workDate}`;
            const params = new URLSearchParams();
            if (scopeName) params.set('scope', scopeName);
            if (scopeId) params.set('excludeScopeId', scopeId);
            if (projectActivities?.length) {
                params.set('scopes', JSON.stringify(projectActivities));
            }
            const qs = params.toString();
            const url = qs ? `${path}?${qs}` : path;
            const authConfig = await getAuthConfig();
            let res = await axios.get(url, authConfig);
            if (
                requestSeq === laborRequestSeqRef.current &&
                res.status === 503 &&
                /rate limit/i.test(String(res.data?.message || ''))
            ) {
                await new Promise((r) => window.setTimeout(r, 2000));
                if (requestSeq !== laborRequestSeqRef.current) return;
                res = await axios.get(url, authConfig);
            }
            if (requestSeq !== laborRequestSeqRef.current) return;
            if (res.data?.status === 'success' && res.data?.data) {
                setData(res.data.data);
            } else {
                setData(null);
                setError(res.data?.message || 'No data found');
            }
        } catch (err) {
            if (requestSeq !== laborRequestSeqRef.current) return;
            const status = err.response?.status;
            const msg = err.response?.data?.message || err.message || 'Failed to load data';
            if (status === 503 && /rate limit/i.test(String(msg))) {
                try {
                    await new Promise((r) => window.setTimeout(r, 2000));
                    if (requestSeq !== laborRequestSeqRef.current) return;
                    const base = process.env.REACT_APP_BACKEND_BASEURL;
                    const path = `${base}/api/miter/production-performance/${projectCode}/labor-entry/${workDate}`;
                    const params = new URLSearchParams();
                    if (scopeName) params.set('scope', scopeName);
                    if (scopeId) params.set('excludeScopeId', scopeId);
                    if (projectActivities?.length) {
                        params.set('scopes', JSON.stringify(projectActivities));
                    }
                    const qs = params.toString();
                    const retryUrl = qs ? `${path}?${qs}` : path;
                    const retryRes = await axios.get(retryUrl, await getAuthConfig());
                    if (requestSeq !== laborRequestSeqRef.current) return;
                    if (retryRes.data?.status === 'success' && retryRes.data?.data) {
                        setData(retryRes.data.data);
                        return;
                    }
                } catch (retryErr) {
                    // fall through to error below
                }
            }
            setData(null);
            setError(msg);
        } finally {
            if (!silent && requestSeq === laborRequestSeqRef.current) {
                setLoading(false);
            }
        }
    }, [projectCode, workDate, scopeName, scopeId, projectActivities, isReady, getAuthConfig]);

    const fetchOtherActivityLabor = React.useCallback(async () => {
        if (!isReady) return;
        if (!projectCode || !/^\d{4}-\d{2}-\d{2}$/.test(workDate)) {
            setOtherActivityLabor(null);
            return;
        }
        setOtherActivityLoading(true);
        try {
            const base = process.env.REACT_APP_BACKEND_BASEURL;
            const path = `${base}/api/miter/production-performance/${encodeURIComponent(projectCode)}/other-activity-labor/${workDate}`;
            const params = new URLSearchParams();
            if (scopeName) params.set('excludeScope', scopeName);
            if (scopeId) params.set('excludeScopeId', scopeId);
            if (projectActivities?.length) {
                params.set('scopes', JSON.stringify(projectActivities));
            }
            const qs = params.toString();
            const url = qs ? `${path}?${qs}` : path;
            const res = await axios.get(url, await getAuthConfig());
            if (res.data?.status === 'success' && res.data?.data) {
                setOtherActivityLabor(res.data.data);
            } else {
                setOtherActivityLabor({ workDate, activities: [] });
            }
        } catch {
            setOtherActivityLabor({ workDate, activities: [] });
        } finally {
            setOtherActivityLoading(false);
        }
    }, [projectCode, workDate, scopeName, scopeId, projectActivities, isReady, getAuthConfig]);

    // Fetch when modal opens (immediate) or when user has finished selecting a date (debounced)
    useEffect(() => {
        if (!isOpen || !projectCode || !isReady) {
            setData(null);
            setError(null);
            setOtherActivityLabor(null);
            setOtherActivityLoading(false);
            setLoading(false);
            lastFetchedDateRef.current = null;
            lastFetchedScopeRef.current = null;
            return;
        }

        const validDate = /^\d{4}-\d{2}-\d{2}$/.test(workDate);
        if (!validDate) return;

        const scopeKey = `${scopeName || ''}|${scopeId || ''}`;
        const scopeChanged =
            lastFetchedScopeRef.current != null && lastFetchedScopeRef.current !== scopeKey;
        lastFetchedScopeRef.current = scopeKey;

        // If the parent signals an update (e.g., Update Multiple just saved),
        // force a re-fetch even if the workDate hasn't changed.
        if (refreshNonce !== lastRefreshNonceRef.current) {
            lastRefreshNonceRef.current = refreshNonce;
            lastFetchedDateRef.current = null;
            fetchLaborEntry();
            return;
        }

        if (scopeChanged) {
            lastFetchedDateRef.current = null;
            fetchLaborEntry();
            return;
        }

        const isInitialLoad = lastFetchedDateRef.current === null;
        if (isInitialLoad) {
            lastFetchedDateRef.current = workDate;
            fetchLaborEntry();
            return;
        }

        const timeoutId = window.setTimeout(() => {
            lastFetchedDateRef.current = workDate;
            fetchLaborEntry();
        }, 400);

        return () => window.clearTimeout(timeoutId);
    }, [isOpen, projectCode, workDate, scopeName, scopeId, fetchLaborEntry, refreshNonce, isReady]);

    // Other Activity Labor loads after the main grid (does not block "Loading labor data...").
    useEffect(() => {
        if (!isOpen || !projectCode || loading || error || !data) {
            if (!isOpen) setOtherActivityLabor(null);
            return;
        }
        const validDate = /^\d{4}-\d{2}-\d{2}$/.test(workDate);
        if (!validDate) return;
        fetchOtherActivityLabor();
    }, [isOpen, projectCode, workDate, scopeName, scopeId, loading, error, data, fetchOtherActivityLabor, refreshNonce]);

    // Initialize editable hours from API data when data loads (use description index so desc-0 always maps to first row)
    useEffect(() => {
        if (!data) {
            setEditableHours({});
            return;
        }
        const grid = data.laborGrid || [];
        const descriptions = data.laborDescriptions || [];
        const next = {};
        if (descriptions.length > 0) {
            descriptions.forEach((desc, idx) => {
                const activityId = `desc-${idx}`;
                const base = grid[idx] || {};
                const hoursByEmployee = base.hoursByEmployee || {};
                const normalized = {};
                Object.entries(hoursByEmployee).forEach(([k, v]) => {
                    normalized[String(k)] = v;
                });
                next[activityId] = normalized;
            });
        } else {
            grid.forEach((row) => {
                const id = row.activityId;
                if (id) {
                    const hoursByEmployee = row.hoursByEmployee || {};
                    const normalized = {};
                    Object.entries(hoursByEmployee).forEach(([k, v]) => {
                        normalized[String(k)] = v;
                    });
                    next[id] = normalized;
                }
            });
        }
        const rows = buildLaborRowsFromGrid(grid, descriptions);
        const emps = data?.employees || [];
        setEditableHours(reconcileLaborHoursToTargets(next, rows, emps));
    }, [data]);

    // Lock body scroll when modal is open
    useEffect(() => {
        if (!isOpen) return;
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, [isOpen]);

    // Fetch non-project labor (jobs with code "X") for the selected work date
    useEffect(() => {
        if (!isOpen || !projectCode) {
            setNonProjectLabor(null);
            return;
        }
        // Avoid firing this in parallel with labor-entry load; staggering requests
        // reduces burst pressure on backend/Miter for first-open past-date lookups.
        if (loading) return;
        const validDate = /^\d{4}-\d{2}-\d{2}$/.test(workDate);
        if (!validDate) return;

        let cancelled = false;
        const requestSeq = ++nonProjectRequestSeqRef.current;
        const timeoutId = window.setTimeout(async () => {
            setNonProjectLaborLoading(true);
            try {
                const base = process.env.REACT_APP_BACKEND_BASEURL;
                const res = await axios.get(
                    `${base}/api/miter/production-performance/${encodeURIComponent(projectCode)}/non-project-labor/${workDate}`,
                    await getAuthConfig()
                );
                if (!cancelled && requestSeq === nonProjectRequestSeqRef.current && res.data?.status === 'success') {
                    setNonProjectLabor(res.data.data);
                }
            } catch (err) {
                if (!cancelled && requestSeq === nonProjectRequestSeqRef.current) setNonProjectLabor(null);
            } finally {
                if (!cancelled && requestSeq === nonProjectRequestSeqRef.current) setNonProjectLaborLoading(false);
            }
        }, 1200);

        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
        };
    }, [isOpen, projectCode, workDate, nplRefreshNonce, loading, isReady, getAuthConfig]);

    // When Add Labor Type popup opens, fetch labor scope codes from API and clear selection
    useEffect(() => {
        if (!isAddLaborOpen || !isReady) return;
        setSelectedLaborTypes([]);
        setLaborScopeCodesError(null);
        const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
        if (!baseUrl) {
            setLaborScopeCodesError('Backend URL not configured.');
            return;
        }
        setLaborScopeCodesLoading(true);
        const qs = projectCode ? `?projectCode=${encodeURIComponent(projectCode)}` : '';
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(
                    `${baseUrl}/api/miter/production-performance/labor-scope-codes${qs}`,
                    await getAuthConfig()
                );
                if (cancelled) return;
                if (res.data?.status === 'success' && Array.isArray(res.data?.data)) {
                    setLaborScopeCodesFromApi(res.data.data);
                } else {
                    setLaborScopeCodesFromApi([]);
                }
            } catch (err) {
                if (cancelled) return;
                setLaborScopeCodesFromApi([]);
                setLaborScopeCodesError(err.response?.data?.message || err.message || 'Failed to load labor types.');
            } finally {
                if (!cancelled) setLaborScopeCodesLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isAddLaborOpen, projectCode, isReady, getAuthConfig]);

    // Auto-clear save messages: errors after 15s, success after 5s.
    useEffect(() => {
        if (!saveMessage) return undefined;
        const delayMs = saveMessage.type === 'error' ? 15000 : 5000;
        const timeoutId = window.setTimeout(() => {
            setSaveMessage(null);
        }, delayMs);
        return () => window.clearTimeout(timeoutId);
    }, [saveMessage]);

    // Keep a visible horizontal scrollbar only for employee columns (right section).
    useEffect(() => {
        if (!isOpen) return;
        const frozenWidth = 310; // 130 + 180 for first two sticky columns

        const updateScrollbar = () => {
            const tableEl = tableScrollRef.current;
            if (!tableEl) return;

            const needsHorizontalScroll = tableEl.scrollWidth > tableEl.clientWidth;
            setShowCustomScrollbar(needsHorizontalScroll);

            const customWidth = Math.max(0, tableEl.scrollWidth - frozenWidth);
            setCustomScrollbarContentWidth(customWidth);
        };

        const rafId = window.requestAnimationFrame(updateScrollbar);
        window.addEventListener('resize', updateScrollbar);
        return () => {
            window.cancelAnimationFrame(rafId);
            window.removeEventListener('resize', updateScrollbar);
        };
    }, [isOpen, data, nonProjectLaborLoading, nonProjectLabor, showCustomScrollbar]);

    if (!isOpen) return null;

    // Extract data for rendering
    const job = data?.job || {};
    const employees = sortEmployeesByHoursToAllocate(data?.employees);
    const laborGrid = data?.laborGrid || [];
    const totalCrewHours = data?.totalCrewHours || 0;
    const allocatedHoursPerEmployee = data?.allocatedHoursPerEmployee || {};
    const laborDescriptions = data?.laborDescriptions || [];
    const scopeEntry = scopeName && Array.isArray(laborScopeCodesFromApi)
        ? laborScopeCodesFromApi.find((e) => (e.scope_name || '').trim() === (scopeName || '').trim())
        : null;
    const availableDescriptionOptions = scopeEntry
        ? (scopeEntry.scope_descriptions || [])
            .filter((d) => !laborDescriptions.includes(d.description_name))
            .map((d) => ({
                id: `${scopeEntry.scope_code}-${d.description_code}`,
                label: d.description_name,
            }))
        : [];
    const selectedDescriptionRows = availableDescriptionOptions
        .filter((opt) => (selectedLaborTypes || []).includes(opt.id))
        .map((opt) => {
            const [scopeCode, ...descCodeParts] = String(opt.id || '').split('-');
            return {
                code: descCodeParts.join('-') || scopeCode || 'USR',
                descriptionName: opt.label,
            };
        });

    const laborRows = buildLaborRowsFromGrid(laborGrid, laborDescriptions);

    // Allocated hours = sum of project labor row cells only (must match laborRows)
    const getAllocatedHours = (empId) => {
        let sum = 0;
        laborRows.forEach((row) => {
            const n = Number(editableHours[row.activityId]?.[empId]) || 0;
            sum += Math.max(0, n);
        });
        return round2(sum);
    };

    const getUnallocatedHours = (empId) => {
        const emp = employees.find(e => e.id === empId);
        const total = emp?.totalHours || 0;
        const allocated = getAllocatedHours(empId);
        const raw = round2(total - allocated);
        // Hide ±0.01-style rounding noise from the grid
        if (Math.abs(raw) <= 0.02) return 0;
        return raw;
    };

    const getNonProjectHoursForEmployee = (empId) => {
        if (!nonProjectLabor?.jobs?.length) return 0;
        const empKey = empId != null ? String(empId) : '';
        return nonProjectLabor.jobs.reduce((sum, job) => {
            const hrs = job.hoursByEmployee?.[empKey] ?? job.hoursByEmployee?.[empId] ?? 0;
            return sum + Number(hrs);
        }, 0);
    };

    const handleHoursChange = (rowActivityId, empId, value) => {
        if (!isValidPartialHoursInput(value)) return;
        setEditableHours((prev) => {
            const row = { ...(prev[rowActivityId] || {}) };
            row[empId] = value;
            return { ...prev, [rowActivityId]: row };
        });
        setSaveMessage(null);
    };

    const handleHoursBlur = (rowActivityId, empId) => {
        setEditableHours((prev) => {
            const row = prev[rowActivityId];
            if (!row || !Object.prototype.hasOwnProperty.call(row, empId)) return prev;
            const normalized = normalizeHoursOnBlur(row[empId]);
            if (row[empId] === normalized) return prev;
            return { ...prev, [rowActivityId]: { ...row, [empId]: normalized } };
        });
    };

    const handleLaborInputEnterKey = (event) => {
        if (event.key !== 'Enter') return;

        // Down the same column; after the last row, jump to the first row of the next employee column.
        event.preventDefault();
        const el = event.currentTarget;
        const rowIdx = Number(el.getAttribute('data-labor-row-index'));
        const colIdx = Number(el.getAttribute('data-labor-col-index'));
        if (!Number.isFinite(rowIdx) || !Number.isFinite(colIdx)) return;
        const sel = (r, c) =>
            document.querySelector(
                `input[data-labor-hours-input="true"][data-labor-row-index="${r}"][data-labor-col-index="${c}"]:not([disabled])`
            );
        const downSameCol = sel(rowIdx + 1, colIdx);
        const nextInput = downSameCol || sel(0, colIdx + 1);
        if (nextInput) {
            nextInput.focus();
            nextInput.select?.();
        }
    };

    /**
     * Per employee: remaining unallocated = totalHours - sum(project labor rows).
     * Within ±threshold, split tiny residuals equally across rows that already have entered hours
     * (add when under-allocated, subtract when over-allocated). Last eligible row absorbs rounding remainder.
     */
    const distributeRemainingProjectHours = (hoursMap) => {
        const next = cloneHoursMap(hoursMap);
        const EPS = 0.005;

        const getEligibleRows = (empId) =>
            laborRows.filter((row) => {
                const hRaw = next[row.activityId]?.[empId];
                const base = hRaw === '' || hRaw == null ? 0 : Number(hRaw);
                const h = Number.isFinite(base) ? base : 0;
                return h > EPS;
            });

        employees.forEach((emp) => {
            const empId = emp.id;
            const total = Number(emp.totalHours) || 0;
            const allocated = laborRows.reduce((s, row) => {
                const v = next[row.activityId]?.[empId];
                const num = v === '' || v == null ? 0 : Number(v);
                return s + (Number.isFinite(num) ? num : 0);
            }, 0);
            const unalloc = round2(total - allocated);
            if (Math.abs(unalloc) <= EPS) return;
            if (Math.abs(unalloc) >= UNALLOCATED_AUTO_DISTRIBUTION_THRESHOLD) return;
            if (allocated <= EPS) return;

            const eligibleRows = getEligibleRows(empId);
            if (eligibleRows.length === 0) return;

            const isOverAllocated = unalloc < -EPS;
            const amount = Math.abs(unalloc);
            let remaining = amount;

            eligibleRows.forEach((row, idx) => {
                const hRaw = next[row.activityId]?.[empId];
                const base = hRaw === '' || hRaw == null ? 0 : Number(hRaw);
                const h = Number.isFinite(base) ? base : 0;
                let portion;
                if (idx === eligibleRows.length - 1) {
                    portion = round2(Math.max(0, remaining));
                } else {
                    portion = round2(amount / eligibleRows.length);
                    remaining = round2(remaining - portion);
                    remaining = Math.max(0, remaining);
                }
                if (!next[row.activityId]) next[row.activityId] = {};
                next[row.activityId][empId] = isOverAllocated
                    ? round2(Math.max(0, h - portion))
                    : round2(Math.max(0, h + portion));
            });
        });
        return reconcileLaborHoursToTargets(next, laborRows, employees);
    };

    const buildLaborEntries = (hoursMap) => {
        const entries = [];
        laborRows.forEach((row) => {
            employees.forEach((emp) => {
                const hours = hoursMap[row.activityId]?.[emp.id];
                const num = hours === '' || hours == null ? 0 : Number(hours);
                if (num >= 0) {
                    entries.push({
                        employeeId: emp.id,
                        employeeName: emp.name,
                        activityKey: row.activityId,
                        laborDescription: row.activityLabel || null,
                        hours: num,
                    });
                }
            });
        });
        return entries;
    };

    const performSave = async (hoursMap) => {
        if (!projectCode || !workDate || !scopeName) {
            setSaveMessage({ type: 'error', text: 'Project, date, and scope are required.' });
            return false;
        }
        if (!data || employees.length === 0) {
            setSaveMessage({ type: 'error', text: 'No timesheet data for this date. Change Work Date to save.' });
            return false;
        }
        const map = reconcileLaborHoursToTargets(hoursMap, laborRows, employees);
        setEditableHours(map);
        setSaving(true);
        setSaveMessage(null);
        try {
            const entries = buildLaborEntries(map);
            const base = process.env.REACT_APP_BACKEND_BASEURL;
            const url = `${base}/api/miter/production-performance/${encodeURIComponent(projectCode)}/labor-entry/save`;
            await axios.post(url, {
                workDate,
                scope: scopeName,
                entries,
            }, await getAuthConfig());
            setSaveMessage({ type: 'success', text: 'Labor allocation saved.' });
            return true;
        } catch (err) {
            setSaveMessage({
                type: 'error',
                text: err.response?.data?.message || err.message || 'Failed to save',
            });
            return false;
        } finally {
            setSaving(false);
        }
    };

    const hasAutoDistributableUnallocated = () => {
        const EPS = 0.005;
        const maxAuto = UNALLOCATED_AUTO_DISTRIBUTION_THRESHOLD - EPS;
        return employees.some((emp) => {
            const u = getUnallocatedHours(emp.id);
            return (u > EPS && u < maxAuto) || (u < -EPS && u > -maxAuto);
        });
    };

    const getBlockingUnallocatedEmployeeNames = () => {
        const EPS = 0.005;
        return employees
            .filter((emp) => getUnallocatedHours(emp.id) >= (UNALLOCATED_AUTO_DISTRIBUTION_THRESHOLD - EPS))
            .map((emp) => emp.name);
    };

    const getBlockingOverAllocatedEmployeeNames = () => {
        const EPS = 0.005;
        return employees
            .filter((emp) => getUnallocatedHours(emp.id) <= -(UNALLOCATED_AUTO_DISTRIBUTION_THRESHOLD - EPS))
            .map((emp) => emp.name);
    };

    const getEmployeeChipLabel = (fullName) => fullName?.trim().split(/\s+/)[0] || fullName;

    const dismissValidationBlock = (blockIndex) => {
        setSaveMessage((prev) => {
            if (!prev?.validationBlocks?.length) return null;
            const remaining = prev.validationBlocks.filter((_, i) => i !== blockIndex);
            if (remaining.length === 0) return null;
            return { ...prev, validationBlocks: remaining };
        });
    };

    const getAllocationValidationBannerContent = (block) => {
        const count = block.employeeNames?.length ?? 0;
        const employeeLabel = count === 1 ? 'employee has' : 'employees have';
        const threshold = UNALLOCATED_AUTO_DISTRIBUTION_THRESHOLD.toFixed(2);

        if (block.messageVariant === 'overAllocated') {
            return {
                title: `Cannot save — ${count} ${employeeLabel} over-allocated hours`,
                subtitle: `Review assigned hours below. Auto-adjustment applies only when extra allocated hours are greater than -${threshold}.`,
            };
        }
        return {
            title: `Cannot save — ${count} ${employeeLabel} unallocated hours`,
            subtitle: `Assign remaining hours below. Hours under ${threshold} will be auto-distributed on save.`,
        };
    };

    const runSaveValidations = () => {
        if (!projectCode || !workDate || !scopeName) {
            setSaveMessage({ type: 'error', text: 'Project, date, and scope are required.' });
            return null;
        }
        if (!data || employees.length === 0) {
            setSaveMessage({ type: 'error', text: 'No timesheet data for this date. Change Work Date to save.' });
            return null;
        }
        const blockingOverAllocatedNames = getBlockingOverAllocatedEmployeeNames();
        const blockingUnallocatedNames = getBlockingUnallocatedEmployeeNames();
        const validationBlocks = [];
        if (blockingOverAllocatedNames.length > 0) {
            validationBlocks.push({ messageVariant: 'overAllocated', employeeNames: blockingOverAllocatedNames });
        }
        if (blockingUnallocatedNames.length > 0) {
            validationBlocks.push({ messageVariant: 'unallocatedRemaining', employeeNames: blockingUnallocatedNames });
        }
        if (validationBlocks.length > 0) {
            setSaveMessage({ type: 'error', validationBlocks });
            return null;
        }
        if (hasAutoDistributableUnallocated()) {
            const next = distributeRemainingProjectHours(editableHours);
            setEditableHours(next);
            return next;
        }
        return editableHours;
    };

    const handleSaveAndLogProductionClick = async () => {
        const hoursMap = runSaveValidations();
        if (!hoursMap) return;

        const saved = await performSave(hoursMap);
        if (!saved) return;

        onLogProduction({
            projectNo: projectCode,
            projectName: projectName ?? '',
            workDate,
            division: scopeName ?? '',
            scopeId: activeRowData?.scopeId ?? activeRowData?.originalData?.scopeId ?? '',
        });
    };

    const handleAddLaborTypesUpdate = async () => {
        if (!scopeName || selectedDescriptionRows.length === 0) {
            setIsAddLaborOpen(false);
            return;
        }
        if (!projectCode || !workDate || employees.length === 0) {
            setSaveMessage({ type: 'error', text: 'No timesheet data for this date. Change Work Date to add labor types.' });
            return;
        }

        setIsAddingLaborTypes(true);
        setSaveMessage(null);
        try {
            const existingDescriptions = Array.isArray(laborDescriptions) ? laborDescriptions : [];
            const existingSet = new Set(existingDescriptions.map((d) => String(d || '').trim().toLowerCase()));
            const labelsToAdd = selectedDescriptionRows
                .map((row) => String(row?.descriptionName || '').trim())
                .filter((label) => label && !existingSet.has(label.toLowerCase()));
            if (labelsToAdd.length === 0) {
                setIsAddLaborOpen(false);
                setSelectedLaborTypes([]);
                setSaveMessage({ type: 'success', text: 'Selected labor type(s) already exist for this date.' });
                return;
            }

            const nextDescriptions = [...existingDescriptions, ...labelsToAdd];
            const nextRows = buildLaborRowsFromGrid(laborGrid, nextDescriptions);
            const nextEditableHours = cloneHoursMap(editableHours);

            nextRows.forEach((row) => {
                if (!nextEditableHours[row.activityId]) nextEditableHours[row.activityId] = {};
                employees.forEach((emp) => {
                    const current = nextEditableHours[row.activityId]?.[emp.id];
                    const value = current === '' || current == null ? 0 : Number(current);
                    nextEditableHours[row.activityId][emp.id] = Number.isFinite(value) ? Math.max(0, value) : 0;
                });
            });

            const entries = [];
            nextRows.forEach((row) => {
                employees.forEach((emp) => {
                    entries.push({
                        employeeId: emp.id,
                        employeeName: emp.name,
                        activityKey: row.activityId,
                        laborDescription: row.activityLabel || null,
                        hours: Number(nextEditableHours[row.activityId]?.[emp.id]) || 0,
                    });
                });
            });

            const base = process.env.REACT_APP_BACKEND_BASEURL;
            const saveUrl = `${base}/api/miter/production-performance/${encodeURIComponent(projectCode)}/labor-entry/save`;
            await axios.post(saveUrl, {
                workDate,
                scope: scopeName,
                entries,
            }, await getAuthConfig());

            await fetchLaborEntry({ silent: true });
            setSelectedLaborTypes([]);
            setIsAddLaborOpen(false);
            setSaveMessage({ type: 'success', text: 'Labor type(s) added for selected work date.' });
        } catch (err) {
            setSaveMessage({
                type: 'error',
                text: err.response?.data?.message || err.message || 'Failed to add labor types.',
            });
        } finally {
            setIsAddingLaborTypes(false);
        }
    };

    const totalUnallocated = employees.reduce((sum, emp) => sum + getUnallocatedHours(emp.id), 0);
    /** Only disable while saving — keep clickable when validations fail so errors are visible on click. */
    const isSaveAndLogProductionDisabled = saving;

    const totalHoursFooterGrand =
        employees.length > 0
            ? Math.round(
                  employees.reduce(
                      (sum, emp) =>
                          sum +
                          (Number(emp.totalHours) || 0) +
                          getNonProjectHoursForEmployee(emp.id),
                      0
                  ) * 100
              ) / 100
            : 0;

    const laborTableColSpan = 2 + employees.length;
    const laborSectionHeadingClass =
        'bg-gray-300 border-b border-gray-300 px-3 py-2 text-center text-sm font-bold text-[#2C2C2C]';
    /** Vertical grid lines between all columns (incl. after col 1 & 2 into employee columns). */
    const laborCellV = 'border-r border-gray-300';
    // Mobile: columns should scroll with the rest of the table.
    // Desktop/tablet: keep first two columns sticky for easier comparison.
    const stickyColOne = 'min-w-[130px] sm:sticky sm:left-0 sm:z-20';
    const stickyColTwo = 'min-w-[180px] sm:sticky sm:left-[130px] sm:z-20';

    return createPortal(
        <div className="fixed inset-0 z-[10160] items-center justify-center bg-black/50">
            {/* inset-2 on phones incl. landscape (≥sm width); lg+ desktop keeps inset-6 */}
            <div className="absolute inset-2 lg:inset-6 bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden">

                {/* Header */}
                <div className="relative z-10 flex items-center justify-between gap-4 bg-white py-[18px] px-6 rounded-t-xl border-b border-gray-200 shadow-[0_2px_5px_rgba(0,0,0,0.10)]">
                    <h2 className="font-poppins text-[20px] font-bold text-[#2C2C2C]">
                        Labor Tracking
                    </h2>

                    <button
                        onClick={onClose}
                        aria-label="Close"
                        className="flex items-center justify-center w-9 h-9 rounded-md hover:bg-gray-100"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" className="shrink-0">
                            <path d="M17.7072 0.292849C17.3167 -0.0976164 16.6836 -0.0976164 16.2932 0.292849L8.99999 7.58604L1.70683 0.292849C1.31637 -0.0976164 0.683306 -0.0976164 0.29285 0.292849C-0.0976166 0.683305 -0.0976166 1.31637 0.29285 1.70683L7.58602 9.00001L0.29287 16.2931C-0.0975966 16.6836 -0.0975966 17.3166 0.29287 17.7072C0.683326 18.0976 1.31639 18.0976 1.70685 17.7072L8.99999 10.414L16.2932 17.7072C16.6836 18.0976 17.3167 18.0976 17.7072 17.7072C18.0976 17.3166 18.0976 16.6836 17.7072 16.2932L10.414 9.00001L17.7072 1.70683C18.0976 1.31637 18.0976 0.683305 17.7072 0.292849Z" fill="#424143" />
                        </svg>
                    </button>
                </div>

                {saveMessage?.validationBlocks?.length > 0 && (
                    <div className="flex shrink-0 flex-col gap-2 border-b border-gray-200 bg-white px-6 py-3">
                        {saveMessage.validationBlocks.map((block, idx) => {
                            const { title, subtitle } = getAllocationValidationBannerContent(block);
                            return (
                                <div
                                    key={idx}
                                    role="alert"
                                    className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                        className="mt-0.5 h-5 w-5 shrink-0 text-amber-500"
                                        aria-hidden="true"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    <div className="min-w-0 flex-1">
                                        <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm font-semibold text-amber-900">
                                            <span>{title}</span>
                                            {block.employeeNames?.map((name) => (
                                                <span
                                                    key={name}
                                                    title={name}
                                                    className="inline-flex cursor-default items-center rounded-full border border-amber-200 bg-white px-2.5 py-0.5 text-xs font-medium text-amber-900"
                                                >
                                                    {getEmployeeChipLabel(name)}
                                                </span>
                                            ))}
                                        </p>
                                        <p className="mt-0.5 text-sm text-amber-800/90">{subtitle}</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => dismissValidationBlock(idx)}
                                        className="shrink-0 text-sm text-amber-700 underline hover:text-amber-900"
                                    >
                                        Dismiss
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto">

                    {/* Project Info */}
                    <section className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 px-4 md:px-6 pt-4 pb-1 bg-white">
                        <div className="flex flex-col gap-1">
                            <label className="text-[14px] font-normal text-[#424143] font-poppins">Project No.</label>
                            <input
                                value={job.code || projectCode || ''}
                                readOnly
                                className="h-10 rounded-md border border-gray-300 px-3 text-sm text-[#2C2C2C] focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="text-[14px] font-normal text-[#424143] font-poppins">Project Name</label>
                            <input
                                value={job.name || 'Loading...'}
                                readOnly
                                className="h-10 rounded-md border border-gray-300 px-3 text-sm text-[#2C2C2C] focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                        </div>

                        <NativeDateField
                            label="Work Date"
                            value={workDate}
                            onChange={setWorkDate}
                        />

                        <div className="flex flex-col gap-1">
                            <label className="text-[14px] font-normal text-[#424143] font-poppins">Activity</label>
                            <input
                                value={scopeName || job.division || '--'}
                                readOnly
                                className="h-10 rounded-md border border-gray-300 px-3 text-sm text-[#2C2C2C] focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                        </div>
                    </section>

                    {/* Update Multiple */}
                    <div className="flex justify-end px-6 pt-1 pb-3">
                        <button
                            type="button"
                            onClick={() => {
                                onOpenUpdateMultiple?.({
                                    projectCode,
                                    workDate,
                                    scopeName: scopeName ?? '',
                                    scopeId: scopeId ?? null,
                                });
                            }}
                            className="flex items-center gap-2 text-[16px] font-normal text-[#AD2226] underline font-poppins leading-none focus:outline-none"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-6 h-6" fill="none">
                                <path d="M10 6C10 7.10457 10.8954 8 12 8C13.1046 8 14 7.10457 14 6C14 4.89543 13.1046 4 12 4C10.8954 4 10 4.89543 10 6Z" fill="#AD2226" />
                                <path d="M10 12C10 13.1046 10.8954 14 12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12Z" fill="#AD2226" />
                                <path d="M12 20C10.8954 20 10 19.1046 10 18C10 16.8954 10.8954 16 12 16C13.1046 16 14 16.8954 14 18C14 19.1046 13.1046 20 12 20Z" fill="#AD2226" />
                            </svg>
                            <span>Update Multiple</span>
                        </button>
                    </div>

                    {/* Loading / Error / Table */}
                    <div className="px-6">
                        {loading && (
                            <div className="py-12 text-center text-gray-500">Loading labor data...</div>
                        )}
                        
                        {!loading && error && (
                            <div className="py-12 text-center text-red-600">{error}</div>
                        )}

                        {!loading && !error && data && (
                            <>
                                {employees.length === 0 && (
                                    <p className="mb-2 text-sm text-gray-500">
                                        No timesheet data for {isoToMDY(workDate)} in Miter. Change Work Date to see employee hours.
                                    </p>
                                )}
                                <div className="relative border border-gray-300 rounded-md overflow-hidden">
                                    <div
                                        ref={tableScrollRef}
                                        onScroll={(e) => {
                                            if (isSyncingScrollRef.current) return;
                                            if (!customScrollRef.current) return;
                                            isSyncingScrollRef.current = true;
                                            customScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
                                            isSyncingScrollRef.current = false;
                                        }}
                                        className="overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                                    >
                                    <table className="w-full min-w-max border-collapse text-sm">
                                        <thead className="sticky top-0 bg-gray-200 z-10">
                                            <tr>
                                                <th className={`bg-[rgb(229_231_235)] p-2 whitespace-nowrap ${laborCellV} ${stickyColOne} z-30`}>Total Crew Hours</th>
                                                <th className={`bg-[#FFF2D3] p-2 text-center align-middle whitespace-nowrap ${laborCellV} ${stickyColTwo} z-30`}>Employee:</th>
                                                {employees.map((emp) => (
                                                    <th
                                                        key={emp.id}
                                                        className={`p-2 text-blue-600 underline cursor-pointer hover:text-blue-800 whitespace-nowrap ${laborCellV}`}
                                                        onClick={(event) => {
                                                            const rect = event.currentTarget.getBoundingClientRect();
                                                            setPopupPosition({
                                                                x: Math.min(rect.left, window.innerWidth - 280),
                                                                y: rect.bottom + 8,
                                                            });
                                                            setActiveEmployee(emp);
                                                        }}
                                                    >
                                                        {emp.name}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {/* Section: Project Labor — covers Hours to Allocate, Unallocated, and activity grid */}
                                            <tr>
                                                <td colSpan={laborTableColSpan} className={`${laborSectionHeadingClass} relative`}>
                                                    <div className="sticky left-1/2 right-1/2 -translate-x-1/2 w-max mx-auto text-center whitespace-nowrap">
                                                        Project Labor
                                                    </div>
                                                </td>
                                            </tr>

                                            {/* Hours to Allocate */}
                                            <tr className="border-b border-gray-300">
                                                <td className={`bg-gray-200 text-center font-semibold ${laborCellV} ${stickyColOne}`}>{totalCrewHours.toFixed(2)}</td>
                                                <td className={`bg-[#FFF2D3] p-2 text-center align-middle whitespace-nowrap ${laborCellV} ${stickyColTwo}`}>Hours to Allocate</td>
                                                {employees.map((emp) => (
                                                    <td key={emp.id} className={`text-center font-semibold ${laborCellV}`}>
                                                        {emp.totalHours.toFixed(2)}
                                                    </td>
                                                ))}
                                            </tr>

                                            {/* Unallocated Hours - total and per-employee calculated; row data in red */}
                                            <tr className="border-b border-gray-300">
                                                <td
                                                    className={`bg-gray-200 text-center text-red-600 ${laborCellV} ${stickyColOne}`}
                                                >
                                                    {totalUnallocated.toFixed(2)}
                                                </td>
                                                <td className={`bg-[#FFF2D3] p-2 text-center align-middle whitespace-nowrap ${laborCellV} ${stickyColTwo}`}>Unallocated Hours</td>
                                                {employees.map((emp) => {
                                                    const unalloc = getUnallocatedHours(emp.id);
                                                    return (
                                                        <td
                                                            key={emp.id}
                                                            className={`text-center text-red-600 ${laborCellV}`}
                                                        >
                                                            {unalloc.toFixed(2)}
                                                        </td>
                                                    );
                                                })}
                                            </tr>

                                            {/* Labor Rows (Activities / Labor Descriptions) - always visible; first row below Unallocated Hours stays empty (no API data in inputs) */}
                                            {laborRows.map((row, rowIndex) => {
                                                const rowHours = employees.reduce(
                                                    (sum, emp) =>
                                                        sum +
                                                        Math.max(0, Number(editableHours[row.activityId]?.[emp.id]) || 0),
                                                    0
                                                );
                                                return (
                                                    <tr key={row.activityId} className="border-b border-gray-300 bg-[#F9FAFB]">
                                                        <td className={`bg-gray-200 text-center ${laborCellV} ${stickyColOne}`}>{(Math.round(rowHours * 100) / 100).toFixed(2)}</td>
                                                        <td className={`bg-[#FFF2D3] p-2 text-center align-middle whitespace-nowrap ${laborCellV} ${stickyColTwo}`}>
                                                            {row.activityLabel}
                                                        </td>
                                                        {employees.map((emp, empIndex) => {
                                                            const empKey = emp.id != null ? String(emp.id) : '';
                                                            const inputValue = getLaborHoursCellValue(
                                                                editableHours[row.activityId],
                                                                empKey,
                                                                emp.id,
                                                                row.hoursByEmployee
                                                            );
                                                            return (
                                                            <td key={emp.id} className={`p-1 ${laborCellV}`}>
                                                                <input
                                                                    type="text"
                                                                    inputMode="decimal"
                                                                    data-labor-hours-input="true"
                                                                    data-labor-row-index={rowIndex}
                                                                    data-labor-col-index={empIndex}
                                                                    value={inputValue}
                                                                    onChange={(e) => {
                                                                        handleHoursChange(row.activityId, emp.id, e.target.value);
                                                                    }}
                                                                    onFocus={(e) => e.target.select()}
                                                                    onBlur={() => handleHoursBlur(row.activityId, emp.id)}
                                                                    onKeyDown={handleLaborInputEnterKey}
                                                                    className="w-full border border-gray-300 rounded px-2 py-1 text-center bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                                />
                                                            </td>
                                                            );
                                                        })}
                                                    </tr>
                                                );
                                            })}

                                            {/* Section: Other Activity Labor */}
                                            <tr className="border-t-2 border-gray-300">
                                                <td colSpan={laborTableColSpan} className={`${laborSectionHeadingClass} relative`}>
                                                    <div className="sticky left-1/2 right-1/2 -translate-x-1/2 w-max mx-auto text-center whitespace-nowrap">
                                                        Other Activity Labor
                                                    </div>
                                                </td>
                                            </tr>

                                            {otherActivityLoading && (
                                                <tr className="border-b border-gray-300">
                                                    <td colSpan={laborTableColSpan} className="relative py-2">
                                                        <div className="sticky left-1/2 right-1/2 -translate-x-1/2 w-max mx-auto text-center text-sm text-gray-500 whitespace-nowrap">
                                                            Loading other activities...
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                            {!otherActivityLoading && otherActivityLabor && otherActivityLabor.activities?.length === 0 && employees.length > 0 && (
                                                <tr className="border-b border-gray-300">
                                                    <td colSpan={laborTableColSpan} className="relative py-2">
                                                        <div className="sticky left-1/2 right-1/2 -translate-x-1/2 w-max mx-auto text-center text-sm text-gray-500 whitespace-nowrap">
                                                            No other activities for this project.
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                            {!otherActivityLoading && otherActivityLabor && (otherActivityLabor.activities || []).map((activity) => {
                                                const matchedTotal = employees.reduce((sum, emp) => {
                                                    const empKey = emp.id != null ? String(emp.id) : '';
                                                    const hrs = activity.hoursByEmployee?.[empKey] ?? activity.hoursByEmployee?.[emp.id] ?? 0;
                                                    return sum + Number(hrs);
                                                }, 0);
                                                const rowKey = activity.scopeId || activity.scope;
                                                return (
                                                    <tr key={rowKey} className="border-b border-gray-300 bg-[#EEF4FF]">
                                                        <td className={`bg-blue-100 text-center font-medium p-2 text-blue-900 ${laborCellV} ${stickyColOne}`}>
                                                            {(Math.round(matchedTotal * 100) / 100).toFixed(2)}
                                                        </td>
                                                        <td className={`bg-cyan-700 p-2 text-center align-middle whitespace-nowrap font-medium ${laborCellV} ${stickyColTwo}`}>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleOpenOtherActivityLabor(activity)}
                                                                className="w-full text-white font-medium whitespace-nowrap cursor-pointer hover:text-cyan-50 hover:underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 rounded-sm"
                                                            >
                                                                {activity.displayName || activity.scope}
                                                            </button>
                                                        </td>
                                                        {employees.map((emp) => {
                                                            const empKey = emp.id != null ? String(emp.id) : '';
                                                            const hrs = activity.hoursByEmployee?.[empKey] ?? activity.hoursByEmployee?.[emp.id] ?? null;
                                                            return (
                                                                <td key={emp.id} className={`text-center p-2 text-blue-700 ${laborCellV}`}>
                                                                    {hrs !== null && Number(hrs) > 0 ? Number(hrs).toFixed(2) : '—'}
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                );
                                            })}

                                            {/* Section: Non-Project Labor */}
                                            <tr className="border-t-2 border-gray-300">
                                                <td colSpan={laborTableColSpan} className={`${laborSectionHeadingClass} relative`}>
                                                    <div className="sticky left-1/2 right-1/2 -translate-x-1/2 w-max mx-auto text-center whitespace-nowrap">
                                                        Non-Project Labor
                                                    </div>
                                                </td>
                                            </tr>

                                            {/* Non-Project Labor rows — appended to the same table, read-only.
                                                Only show hours for employees already in the main table (matched by ID).
                                                Employees not present in non-project labor data show a dash. */}
                                            {nonProjectLaborLoading && employees.length > 0 && (
                                                <tr className="border-b border-gray-300">
                                                    <td colSpan={laborTableColSpan} className="relative py-2">
                                                        <div className="sticky left-1/2 right-1/2 -translate-x-1/2 w-max mx-auto text-center text-sm text-gray-400 italic whitespace-nowrap">
                                                            Loading non-project labor...
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                            {!nonProjectLaborLoading && nonProjectLabor && nonProjectLabor.jobs.length === 0 && employees.length > 0 && (
                                                <tr className="border-b border-gray-300">
                                                    <td colSpan={laborTableColSpan} className="relative py-2">
                                                        <div className="sticky left-1/2 right-1/2 -translate-x-1/2 w-max mx-auto text-center text-sm text-gray-500 whitespace-nowrap">
                                                            No non-project labor recorded on {isoToMDY(workDate)}.
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                            {!nonProjectLaborLoading && nonProjectLabor && nonProjectLabor.jobs.map((job) => {
                                                // Calculate total hours using ONLY employees from the main table
                                                const matchedTotal = employees.reduce((sum, emp) => {
                                                    const empKey = emp.id != null ? String(emp.id) : '';
                                                    const hrs = job.hoursByEmployee?.[empKey] ?? job.hoursByEmployee?.[emp.id] ?? 0;
                                                    return sum + Number(hrs);
                                                }, 0);
                                                const rowKey = `${job.jobId}|${job.activityId || 'none'}`;
                                                return (
                                                    <tr key={rowKey} className="border-b border-gray-300 bg-[#EEF4FF]">
                                                        <td className={`bg-blue-100 text-center font-medium p-2 text-blue-900 ${laborCellV} ${stickyColOne}`}>
                                                            {(Math.round(matchedTotal * 100) / 100).toFixed(2)}
                                                        </td>
                                                        <td className={`bg-[#DBEAFE] p-2 text-center align-middle whitespace-nowrap font-medium text-blue-800 ${laborCellV} ${stickyColTwo}`}>
                                                            {job.displayName || job.jobName}
                                                        </td>
                                                        {employees.map((emp) => {
                                                            const empKey = emp.id != null ? String(emp.id) : '';
                                                            const hrs = job.hoursByEmployee?.[empKey] ?? job.hoursByEmployee?.[emp.id] ?? null;
                                                            return (
                                                                <td key={emp.id} className={`text-center p-2 text-blue-700 ${laborCellV}`}>
                                                                    {hrs !== null && Number(hrs) > 0 ? Number(hrs).toFixed(2) : '—'}
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                );
                                            })}

                                            {/* Total Hours: section heading (same style as other section rows), then numeric summary as last row */}
                                            {employees.length > 0 && (
                                                <>
                                                    <tr className="border-t-2 border-gray-300">
                                                        <td colSpan={laborTableColSpan} className={`${laborSectionHeadingClass} relative`}>
                                                            <div className="sticky left-1/2 right-1/2 -translate-x-1/2 w-max mx-auto text-center whitespace-nowrap">
                                                                Total Hours
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    <tr className="border-b border-gray-300 bg-[#EEF4FF]">
                                                        <td
                                                            className={`bg-blue-100 text-center font-bold p-2 text-blue-900 ${laborCellV} ${stickyColOne}`}
                                                        >
                                                            {totalHoursFooterGrand.toFixed(2)}
                                                        </td>
                                                        <td
                                                            className={`bg-blue-100 p-2 text-center align-middle whitespace-nowrap font-bold text-blue-900 ${laborCellV} ${stickyColTwo}`}
                                                        >
                                                            Total Hours
                                                        </td>
                                                        {employees.map((emp) => {
                                                            const hoursToAllocate = Number(emp.totalHours) || 0;
                                                            const npl = getNonProjectHoursForEmployee(emp.id);
                                                            const cell = Math.round((hoursToAllocate + npl) * 100) / 100;
                                                            return (
                                                                <td
                                                                    key={emp.id}
                                                                    className={`text-center p-2 font-bold text-blue-700 ${laborCellV}`}
                                                                >
                                                                    {cell.toFixed(2)}
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                </>
                                            )}
                                        </tbody>
                                    </table>
                                    </div>
                                    {showCustomScrollbar && (
                                        <div
                                            ref={customScrollRef}
                                            onScroll={(e) => {
                                                if (isSyncingScrollRef.current) return;
                                                if (!tableScrollRef.current) return;
                                                isSyncingScrollRef.current = true;
                                                tableScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
                                                isSyncingScrollRef.current = false;
                                            }}
                                            className="absolute bottom-0 left-[310px] right-0 z-40 h-3 overflow-x-scroll overflow-y-hidden"
                                        >
                                            <div
                                                ref={customScrollInnerRef}
                                                className="h-px"
                                                style={{ width: `${customScrollbarContentWidth}px` }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    <footer className="px-6 pt-8 pb-10">
                        {/* Add Labor Type */}
                        <div className="flex justify-start">
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => setIsAddLaborOpen(true)}
                                    className="h-10 px-6 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    Add Labor Type
                                </button>

                                {isAddLaborOpen && (
                                    <>
                                        <div className="fixed inset-0 z-[10150]" onClick={() => setIsAddLaborOpen(false)} />
                                        <div className="absolute left-0 bottom-12 z-[10160] w-[280px] rounded-lg bg-white border border-gray-200 shadow-xl">
                                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                                                <h3 className="text-sm font-semibold text-gray-900">Add Labor Type</h3>
                                                <button onClick={() => setIsAddLaborOpen(false)} className="text-gray-400 hover:text-gray-600" aria-label="Close">✕</button>
                                            </div>
                                            <div className="px-4 py-3 max-h-[280px] overflow-y-auto">
                                                {(() => {
                                                    // Options from API (labor_scope_codes) for current scope, excluding descriptions already in the table
                                                    if (laborScopeCodesLoading) {
                                                        return <p className="text-sm text-gray-500">Loading labor types…</p>;
                                                    }
                                                    if (laborScopeCodesError) {
                                                        return <p className="text-sm text-red-600">{laborScopeCodesError}</p>;
                                                    }
                                                    if (!scopeName) {
                                                        return <p className="text-sm text-gray-500">No scope selected.</p>;
                                                    }
                                                    if (!scopeEntry) {
                                                        return <p className="text-sm text-gray-500">No labor types defined for this scope.</p>;
                                                    }
                                                    if (availableDescriptionOptions.length === 0) {
                                                        return <p className="text-sm text-gray-500">All labor types for this scope are already in the table.</p>;
                                                    }
                                                    return (
                                                        <>
                                                            <p className="text-xs text-gray-500 mb-3">Select labor types to add:</p>
                                                            <div className="space-y-2">
                                                                {availableDescriptionOptions.map((opt) => (
                                                                    <label key={opt.id} className="flex gap-2 items-center cursor-pointer">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={(selectedLaborTypes || []).includes(opt.id)}
                                                                            onChange={(e) => {
                                                                                setSelectedLaborTypes((prev) => {
                                                                                    const set = new Set(prev || []);
                                                                                    if (e.target.checked) set.add(opt.id);
                                                                                    else set.delete(opt.id);
                                                                                    return Array.from(set);
                                                                                });
                                                                            }}
                                                                            className="rounded border-gray-300"
                                                                        />
                                                                        <span className="text-sm text-gray-800">{opt.label}</span>
                                                                    </label>
                                                                ))}
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                            <div className="px-4 py-3 border-t border-gray-200">
                                                <button
                                                    type="button"
                                                    onClick={handleAddLaborTypesUpdate}
                                                    disabled={isAddingLaborTypes || selectedDescriptionRows.length === 0}
                                                    className="w-full h-9 rounded-md bg-[#AD2226] text-white text-sm font-medium hover:bg-[#8F1B1E] disabled:cursor-not-allowed disabled:opacity-60"
                                                >
                                                    {isAddingLaborTypes ? 'Updating...' : 'Update'}
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Save and Log Production */}
                        <div className="flex flex-col items-center gap-2 mt-6">
                            {saveMessage && !saveMessage.validationBlocks?.length && (
                                <p className={`text-sm ${saveMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                                    {saveMessage.text}
                                </p>
                            )}
                            <button
                                type="button"
                                onClick={handleSaveAndLogProductionClick}
                                disabled={isSaveAndLogProductionDisabled}
                                className={`h-11 px-16 rounded-md text-white text-sm font-medium ${
                                    isSaveAndLogProductionDisabled
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-[#AD2226] hover:bg-[#8F1B1E]'
                                }`}
                            >
                                {saving ? 'Saving...' : 'Save and Log Production'}
                            </button>
                        </div>
                    </footer>

                </div>

            </div>
            
            {/* TimeStamp Popup */}
            <TimeStampPopup
                employee={activeEmployee}
                position={popupPosition}
                onClose={() => setActiveEmployee(null)}
                nonProjectLabor={nonProjectLabor}
                onNplSaved={() => setNplRefreshNonce(n => n + 1)}
            />
        </div>,
        document.body
    );
};

export default LaborTrackingModal;


// TimeStamp Popup Component
const TimeStampPopup = ({ employee, position, onClose, nonProjectLabor, onNplSaved }) => {
    if (!employee) return null;

    const timestamps = employee.timestamps || [];

    // Collect non-project labor timestamps for this employee across all X-code jobs
    const nplSections = [];
    if (nonProjectLabor && nonProjectLabor.jobs) {
        const empKey = employee.id != null ? String(employee.id) : '';
        nonProjectLabor.jobs.forEach(job => {
            const tsForEmp = job.timestampsByEmployee?.[empKey] || job.timestampsByEmployee?.[employee.id] || [];
            if (tsForEmp.length > 0) {
                const totalHours = tsForEmp.reduce((s, t) => s + (t.hours || 0), 0);
                nplSections.push({
                    jobId: job.jobId,
                    jobName: job.jobName,
                    totalHours: Math.round(totalHours * 100) / 100,
                    timestamps: tsForEmp
                });
            }
        });
    }

    return createPortal(
        <>
            <div className="fixed inset-0 z-[10150]" onClick={onClose} />
            <div
                className="fixed z-[10160] w-[380px] rounded-lg bg-white border border-gray-200 shadow-xl max-h-[90vh] overflow-y-auto"
                style={{
                    top: position.y,
                    left: position.x,
                }}
            >
                <div className="flex items-center justify-between px-4 py-3 border-b sticky top-0 bg-white z-10">
                    <h3 className="text-[#2C2C2C] font-poppins text-[16px] font-bold leading-normal">
                        Time Stamp
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
                </div>

                {/* Main table timestamp section */}
                {nplSections.length > 0 && (
                    <div className="px-4 pt-3 pb-1">
                        <span className="inline-block text-xs font-semibold uppercase tracking-wide text-gray-500 bg-gray-100 rounded px-2 py-0.5">
                            Project Labor
                        </span>
                    </div>
                )}
                <TimeStampEditor key={`main-${employee.id}`} employee={employee} timestamps={timestamps} />

                {/* Non-project labor timestamp sections */}
                {nplSections.map(section => (
                    <div key={section.jobId}>
                        <div className="mx-4 border-t-2 border-blue-200 mt-1" />
                        <div className="px-4 pt-3 pb-1">
                            <span className="inline-block text-xs font-semibold uppercase tracking-wide text-blue-700 bg-blue-50 rounded px-2 py-0.5">
                                Non-Project Labor
                            </span>
                            <span className="ml-2 text-xs text-gray-500">{section.jobName}</span>
                        </div>
                        <TimeStampEditor
                            key={`npl-${section.jobId}-${employee.id}`}
                            employee={{ ...employee, totalHours: section.totalHours }}
                            timestamps={section.timestamps}
                            onSaved={onNplSaved}
                        />
                    </div>
                ))}
            </div>
        </>,
        document.body
    );
};

// Normalize time strings to "hh:mm AM/PM".
// Accepts "07:02 AM" or "16:08" and converts to AM/PM for display/picker.
const normalizeDisplayTime = (input) => {
    if (!input || typeof input !== "string") return null;
    const s = input.trim();

    // Already in AM/PM format
    if (/\b(AM|PM)\b/i.test(s)) {
        const m = s.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (!m) return s;
        const hh = String(parseInt(m[1], 10)).padStart(2, "0");
        const mm = m[2];
        const ap = m[3].toUpperCase();
        return `${hh}:${mm} ${ap}`;
    }

    // 24-hour "HH:MM"
    const m24 = s.match(/^(\d{1,2}):(\d{2})$/);
    if (!m24) return s;
    let h = parseInt(m24[1], 10);
    const mm = m24[2];
    if (!Number.isFinite(h)) return s;
    const ap = h >= 12 ? "PM" : "AM";
    h = h % 12;
    if (h === 0) h = 12;
    return `${String(h).padStart(2, "0")}:${mm} ${ap}`;
};

const formatUnixToDisplayTime = (unixSeconds) => {
    if (!unixSeconds) return null;
    const d = new Date(unixSeconds * 1000);
    if (Number.isNaN(d.getTime())) return null;
    // Intentionally use browser/local time to match how Miter dashboard is viewed by the user.
    return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
};

const TimeStampEditor = ({ employee, timestamps, onSaved }) => {
    const { getAuthConfig } = useAuthFetch();
    const [rows, setRows] = useState(
        (timestamps || []).map((ts) => ({
            id: ts.id ?? ts._id ?? null,
            clockInUnix: ts.clockInUnix ?? null,
            clockOutUnix: ts.clockOutUnix ?? null,
            hours: ts.hours ?? null,
            clockIn:
                normalizeDisplayTime(formatUnixToDisplayTime(ts.clockInUnix) ?? ts.clockIn) ?? "--",
            clockOut:
                normalizeDisplayTime(formatUnixToDisplayTime(ts.clockOutUnix) ?? ts.clockOut) ?? "--",
            editIn:
                normalizeDisplayTime(formatUnixToDisplayTime(ts.clockInUnix) ?? ts.clockIn) ?? "",
            editOut:
                normalizeDisplayTime(formatUnixToDisplayTime(ts.clockOutUnix) ?? ts.clockOut) ?? "",
        }))
    );

    // Live total hours — recomputed from rows so it updates immediately after save
    const liveTotalHours = rows.reduce((sum, r) => sum + (r.hours ?? 0), 0);
    const [savingId, setSavingId] = useState(null);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const baseUnixForRow = (row) => row.clockInUnix ?? row.clockOutUnix ?? null;

    const displayTimeToDayjs = (display, baseUnix) => {
        if (!display || typeof display !== "string") return null;
        const parsed = dayjs(display.trim(), ["hh:mm A", "h:mm A", "HH:mm"], true);
        if (!parsed.isValid()) return null;

        const base = baseUnix ? dayjs.unix(baseUnix) : dayjs();
        return base.hour(parsed.hour()).minute(parsed.minute()).second(0).millisecond(0);
    };

    const dayjsToDisplayTime = (val) => {
        if (!val) return "";
        const d = dayjs.isDayjs(val) ? val : dayjs(val);
        if (!d.isValid()) return "";
        return d.format("hh:mm A");
    };

    const handleFieldChange = (index, field, value) => {
        setRows((prev) =>
            prev.map((r, i) =>
                i === index
                    ? {
                          ...r,
                          [field]: value,
                      }
                    : r
            )
        );
        setError(null);
        setSuccess(null);
    };

    const handleSave = async (row, index) => {
        if (!row.id) {
            setError("Cannot update this entry (missing timesheet id).");
            return;
        }
        setSavingId(row.id);
        setError(null);
        setSuccess(null);
        try {
            const baseUnix = row.clockInUnix ?? row.clockOutUnix ?? null;
            const clockInUnix = toUpdatedUnix(baseUnix, row.editIn);
            const clockOutUnix = toUpdatedUnix(baseUnix, row.editOut);

            const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
            const resp = await axios.post(`${baseUrl}/api/miter/production-performance/timesheets/update-clock`, {
                timesheetId: row.id,
                clockInUnix,
                clockOutUnix,
            }, await getAuthConfig());

            // Use persisted values from backend response, fall back to what we sent.
            const persistedClockInUnix = resp?.data?.data?.clockInUnix ?? clockInUnix;
            const persistedClockOutUnix = resp?.data?.data?.clockOutUnix ?? clockOutUnix;
            const persistedHours = resp?.data?.data?.hours ?? row.hours ?? null;
            // Re-derive display from unix (same method as initial load) to stay consistent.
            const persistedClockIn = normalizeDisplayTime(formatUnixToDisplayTime(persistedClockInUnix)) ?? row.editIn ?? "";
            const persistedClockOut = normalizeDisplayTime(formatUnixToDisplayTime(persistedClockOutUnix)) ?? row.editOut ?? "";

            setRows((prev) =>
                prev.map((r, i) =>
                    i === index
                        ? {
                              ...r,
                              clockInUnix: persistedClockInUnix,
                              clockOutUnix: persistedClockOutUnix,
                              hours: persistedHours,
                              clockIn: persistedClockIn,
                              clockOut: persistedClockOut,
                              editIn: persistedClockIn,
                              editOut: persistedClockOut,
                          }
                        : r
                )
            );
            setSuccess("Timestamp updated.");
            if (onSaved) onSaved();
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Failed to update timestamp.");
        } finally {
            setSavingId(null);
        }
    };

    return (
        <div className="px-4 py-3 space-y-3">
            <div className="text-[#424143] font-poppins text-[14px] font-semibold leading-normal">
                {employee.name}
            </div>
            <div className="text-[12px] text-gray-500">
                Total Hours:{" "}
                <span className="font-semibold text-gray-700">
                    {rows.some(r => r.hours != null)
                        ? (Math.round(liveTotalHours * 100) / 100).toFixed(2)
                        : (employee.totalHours?.toFixed(2) ?? '0.00')}
                </span>
            </div>

            {error && <div className="text-xs text-red-600">{error}</div>}
            {success && <div className="text-xs text-green-600">{success}</div>}

            {rows.length === 0 ? (
                <div className="text-sm text-gray-400">No timestamp data</div>
            ) : (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div className="space-y-2">
                        {rows.map((row, idx) => {
                            const baseUnix = baseUnixForRow(row);
                            return (
                                <div key={row.id ?? idx} className="border-t pt-2 pb-2 text-sm space-y-2">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <div className="text-[#808080] font-poppins text-[12px] font-normal">
                                                Clock In
                                            </div>
                                            <TimePicker
                                                value={displayTimeToDayjs(row.editIn || row.clockIn || "", baseUnix)}
                                                onChange={(val) =>
                                                    handleFieldChange(idx, "editIn", dayjsToDisplayTime(val))
                                                }
                                                format="hh:mm A"
                                                ampm
                                                minutesStep={1}
                                                timeSteps={{ minutes: 1 }}
                                                slotProps={{
                                                    textField: {
                                                        size: "small",
                                                        fullWidth: false,
                                                        placeholder: "hh:mm aa",
                                                        error: false,
                                                        sx: {
                                                            width: 168,
                                                            "& .MuiOutlinedInput-input": {
                                                                fontSize: 12,
                                                                paddingTop: "8px",
                                                                paddingBottom: "8px",
                                                                paddingLeft: "10px",
                                                                paddingRight: "10px",
                                                            },
                                                            "& .MuiOutlinedInput-root": {
                                                                // Ensure adornment/icon alignment is consistent.
                                                                alignItems: "center",
                                                            },
                                                            "& .MuiInputAdornment-positionEnd": {
                                                                marginLeft: "auto",
                                                                marginRight: 0,
                                                            },
                                                            "& .MuiIconButton-root": {
                                                                padding: 0,
                                                                marginRight: 0,
                                                            },
                                                            "& .MuiSvgIcon-root": {
                                                                fontSize: 18,
                                                            },
                                                            "& .MuiOutlinedInput-notchedOutline": {
                                                                borderColor: "#D1D5DB", // gray-300
                                                            },
                                                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                                                borderColor: "#9CA3AF", // gray-400
                                                            },
                                                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                                {
                                                                    borderColor: "#3B82F6", // blue-500
                                                                },
                                                        },
                                                    },
                                                    openPickerButton: {
                                                        size: "small",
                                                        sx: { marginRight: 0, padding: 0 },
                                                    },
                                                    openPickerIcon: {
                                                        fontSize: "small",
                                                        sx: { fontSize: 18 },
                                                    },
                                                    actionBar: {
                                                        actions: ["cancel", "accept"],
                                                    },
                                                    popper: {
                                                        disablePortal: true,
                                                        placement: "bottom-start",
                                                        modifiers: [
                                                            { name: "flip", enabled: false },
                                                            {
                                                                name: "preventOverflow",
                                                                options: { boundary: "viewport" },
                                                            },
                                                        ],
                                                        sx: {
                                                            zIndex: 20000,
                                                            "& .MuiPaper-root": {
                                                                width: "fit-content",
                                                                maxWidth: "none",
                                                            },
                                                            "& .MuiPickersLayout-root": {
                                                                width: "fit-content",
                                                                padding: 0,
                                                            },
                                                            "& .MuiPickersLayout-contentWrapper": {
                                                                padding: 0,
                                                            },
                                                            "& .MuiMultiSectionDigitalClock-root": {
                                                                maxHeight: 160,
                                                                width: "fit-content",
                                                            },
                                                            "& .MuiMultiSectionDigitalClockSection-root": {
                                                                width: 56,
                                                                paddingTop: 0,
                                                                paddingBottom: 0,
                                                                scrollbarWidth: "thin", // Firefox
                                                                scrollbarColor: "#9CA3AF transparent",
                                                            },
                                                            "& .MuiMultiSectionDigitalClockSection-root::-webkit-scrollbar": {
                                                                width: 6,
                                                            },
                                                            "& .MuiMultiSectionDigitalClockSection-root::-webkit-scrollbar-track": {
                                                                background: "transparent",
                                                            },
                                                            "& .MuiMultiSectionDigitalClockSection-root::-webkit-scrollbar-thumb": {
                                                                backgroundColor: "#9CA3AF",
                                                                borderRadius: 999,
                                                            },
                                                            "& .MuiMultiSectionDigitalClockSection-root::-webkit-scrollbar-thumb:hover": {
                                                                backgroundColor: "#6B7280",
                                                            },
                                                            "& .MuiMultiSectionDigitalClockSection-item": {
                                                                minHeight: 28,
                                                                height: 28,
                                                                paddingTop: 0,
                                                                paddingBottom: 0,
                                                                fontSize: 13,
                                                                lineHeight: "28px",
                                                            },
                                                            "& .MuiMultiSectionDigitalClockSection-content": {
                                                                paddingTop: 0,
                                                                paddingBottom: 0,
                                                            },
                                                            "& .MuiMultiSectionDigitalClockSection-list": {
                                                                paddingTop: 0,
                                                                paddingBottom: 0,
                                                            },
                                                            "& .MuiDialogActions-root, & .MuiPickersActionBar-root": {
                                                                padding: "4px 8px",
                                                            },
                                                        },
                                                    },
                                                    desktopPaper: {
                                                        sx: {
                                                            zIndex: 20000,
                                                        },
                                                    },
                                                    mobilePaper: {
                                                        sx: {
                                                            zIndex: 20000,
                                                        },
                                                    },
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <div className="text-[#808080] font-poppins text-[12px] font-normal">
                                                Clock Out
                                            </div>
                                            <TimePicker
                                                value={displayTimeToDayjs(row.editOut || row.clockOut || "", baseUnix)}
                                                onChange={(val) =>
                                                    handleFieldChange(idx, "editOut", dayjsToDisplayTime(val))
                                                }
                                                format="hh:mm A"
                                                ampm
                                                minutesStep={1}
                                                timeSteps={{ minutes: 1 }}
                                                slotProps={{
                                                    textField: {
                                                        size: "small",
                                                        fullWidth: false,
                                                        placeholder: "hh:mm aa",
                                                        error: false,
                                                        sx: {
                                                            width: 168,
                                                            "& .MuiOutlinedInput-input": {
                                                                fontSize: 12,
                                                                paddingTop: "8px",
                                                                paddingBottom: "8px",
                                                                paddingLeft: "10px",
                                                                paddingRight: "10px",
                                                            },
                                                            "& .MuiOutlinedInput-root": {
                                                                // Ensure adornment/icon alignment is consistent.
                                                                alignItems: "center",
                                                            },
                                                            "& .MuiInputAdornment-positionEnd": {
                                                                marginLeft: "auto",
                                                                marginRight: 0,
                                                            },
                                                            "& .MuiIconButton-root": {
                                                                padding: 0,
                                                                marginRight: 0,
                                                            },
                                                            "& .MuiSvgIcon-root": {
                                                                fontSize: 18,
                                                            },
                                                            "& .MuiOutlinedInput-notchedOutline": {
                                                                borderColor: "#D1D5DB", // gray-300
                                                            },
                                                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                                                borderColor: "#9CA3AF", // gray-400
                                                            },
                                                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                                {
                                                                    borderColor: "#3B82F6", // blue-500
                                                                },
                                                        },
                                                    },
                                                    openPickerButton: {
                                                        size: "small",
                                                        sx: { marginRight: 0, padding: 0 },
                                                    },
                                                    openPickerIcon: {
                                                        fontSize: "small",
                                                        sx: { fontSize: 18 },
                                                    },
                                                    actionBar: {
                                                        actions: ["cancel", "accept"],
                                                    },
                                                    popper: {
                                                        disablePortal: true,
                                                        placement: "bottom-start",
                                                        modifiers: [
                                                            { name: "flip", enabled: false },
                                                            {
                                                                name: "preventOverflow",
                                                                options: { boundary: "viewport" },
                                                            },
                                                        ],
                                                        sx: {
                                                            zIndex: 20000,
                                                            "& .MuiPaper-root": {
                                                                width: "fit-content",
                                                                maxWidth: "none",
                                                            },
                                                            "& .MuiPickersLayout-root": {
                                                                width: "fit-content",
                                                                padding: 0,
                                                            },
                                                            "& .MuiPickersLayout-contentWrapper": {
                                                                padding: 0,
                                                            },
                                                            "& .MuiMultiSectionDigitalClock-root": {
                                                                maxHeight: 160,
                                                                width: "fit-content",
                                                            },
                                                            "& .MuiMultiSectionDigitalClockSection-root": {
                                                                width: 56,
                                                                paddingTop: 0,
                                                                paddingBottom: 0,
                                                                scrollbarWidth: "thin", // Firefox
                                                                scrollbarColor: "#9CA3AF transparent",
                                                            },
                                                            "& .MuiMultiSectionDigitalClockSection-root::-webkit-scrollbar": {
                                                                width: 6,
                                                            },
                                                            "& .MuiMultiSectionDigitalClockSection-root::-webkit-scrollbar-track": {
                                                                background: "transparent",
                                                            },
                                                            "& .MuiMultiSectionDigitalClockSection-root::-webkit-scrollbar-thumb": {
                                                                backgroundColor: "#9CA3AF",
                                                                borderRadius: 999,
                                                            },
                                                            "& .MuiMultiSectionDigitalClockSection-root::-webkit-scrollbar-thumb:hover": {
                                                                backgroundColor: "#6B7280",
                                                            },
                                                            "& .MuiMultiSectionDigitalClockSection-item": {
                                                                minHeight: 28,
                                                                height: 28,
                                                                paddingTop: 0,
                                                                paddingBottom: 0,
                                                                fontSize: 13,
                                                                lineHeight: "28px",
                                                            },
                                                            "& .MuiMultiSectionDigitalClockSection-content": {
                                                                paddingTop: 0,
                                                                paddingBottom: 0,
                                                            },
                                                            "& .MuiMultiSectionDigitalClockSection-list": {
                                                                paddingTop: 0,
                                                                paddingBottom: 0,
                                                            },
                                                            "& .MuiDialogActions-root, & .MuiPickersActionBar-root": {
                                                                padding: "4px 8px",
                                                            },
                                                        },
                                                    },
                                                    desktopPaper: {
                                                        sx: {
                                                            zIndex: 20000,
                                                        },
                                                    },
                                                    mobilePaper: {
                                                        sx: {
                                                            zIndex: 20000,
                                                        },
                                                    },
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div className="flex justify-end pt-1">
                                        <button
                                            type="button"
                                            onClick={() => handleSave(row, idx)}
                                            disabled={row.id == null || (savingId != null && savingId === row.id)}
                                            className={`px-3 py-1 rounded text-xs text-white ${
                                                savingId != null && savingId === row.id
                                                    ? "bg-gray-400 cursor-not-allowed"
                                                    : "bg-[#AD2226] hover:bg-[#8F1B1E]"
                                            }`}
                                        >
                                            {savingId != null && savingId === row.id ? "Saving..." : "Save"}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </LocalizationProvider>
            )}
        </div>
    );
};

// Merge edited time (12-hour like "07:02 AM") onto the same date as baseUnix.
// Uses dayjs (same as TimePicker) so the date anchor and hour/minute resolution
// are exactly consistent with what the picker displayed.
const toUpdatedUnix = (baseUnix, display) => {
    if (!display || !baseUnix) return baseUnix ?? null;
    if (typeof display !== "string") return baseUnix ?? null;

    const parsed = dayjs(display.trim(), ["hh:mm A", "h:mm A", "HH:mm"], true);
    if (!parsed.isValid()) return baseUnix ?? null;

    // Anchor to the same calendar date as baseUnix in local time, then apply new h/m.
    const base = dayjs.unix(baseUnix);
    const updated = base
        .hour(parsed.hour())
        .minute(parsed.minute())
        .second(0)
        .millisecond(0);
    return updated.unix();
};
